import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FinalCtaSection } from '@/components/food-cost/FinalCtaSection'
import { FoodCostAuthoritySection } from '@/components/food-cost/FoodCostAuthoritySection'
import { FoodCostHeader } from '@/components/food-cost/FoodCostHeader'
import { FORM_HASH } from '@/components/food-cost/constants'
import { learnArticlePath } from '@/features/aprender/articles'
import { LocaleLink } from '@/navigation/LocaleLink'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { SITE_ORIGIN } from '@/navigation/constants'
import { absoluteLocalizedUrl, localizePath } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import '@/styles/food-cost-fonts.css'

import { AppEscandallosSocialProofSection } from './AppEscandallosSocialProofSection'
import { AppEscandallosTimelineSection } from './AppEscandallosTimelineSection'
import { ParaQuienEsAudienceSection } from './ParaQuienEsAudienceSection'
import { SeoKeywordDemoSection } from './SeoKeywordDemoSection'
import { SeoKeywordHero } from './SeoKeywordHero'
import { SeoKeywordPricesSection } from './SeoKeywordPricesSection'
import type { SeoKeywordLandingDefinition } from './catalog'
import { seoKeywordI18nPrefix } from './catalog'

const OG_IMAGE = `${SITE_ORIGIN}/escandallo-restaurante.webp`
const TIPI_ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`

interface SeoKeywordLandingPageProps {
    landing: SeoKeywordLandingDefinition
}

function SeoKeywordLandingSections({ landing }: { landing: SeoKeywordLandingDefinition }) {
    switch (landing.layout) {
        case 'app-timeline':
            return (
                <>
                    <AppEscandallosTimelineSection />
                    <AppEscandallosSocialProofSection />
                </>
            )
        case 'who-its-for':
            return (
                <>
                    <FoodCostAuthoritySection landingPage={landing.landingPage} />
                    <ParaQuienEsAudienceSection />
                </>
            )
        case 'voice-escandallo':
            return (
                <>
                    <SeoKeywordPricesSection />
                    <SeoKeywordDemoSection />
                    {landing.id === 'escandallo-sin-ordenador' ? <SeoKeywordMethodGuideSection /> : null}
                </>
            )
    }
}

function SeoKeywordMethodGuideSection() {
    const { t } = useTranslation()
    const prefix = seoKeywordI18nPrefix('escandallo-sin-ordenador')

    return (
        <section className='bg-[#F5EEE4] px-[22px] pb-10 md:px-10 md:pb-12'>
            <p className='m-0 mx-auto max-w-[720px] text-center text-[15px] leading-[1.6] text-[#5C5548] md:text-base'>
                {t(`${prefix}.methodGuide.before`)}
                <LocaleLink
                    to={learnArticlePath('como-hacer-un-escandallo-paso-a-paso')}
                    className='font-medium text-[#17130E] underline decoration-[#F4502A]/50 underline-offset-4 transition-colors hover:text-[#F4502A]'
                >
                    {t(`${prefix}.methodGuide.link`)}
                </LocaleLink>
                {t(`${prefix}.methodGuide.after`)}
            </p>
        </section>
    )
}

export function SeoKeywordLandingPage({ landing }: SeoKeywordLandingPageProps) {
    const { t } = useTranslation()
    const locale = useLandingLocale()
    const prefix = seoKeywordI18nPrefix(landing.id)
    const postHeroRef = useRef<HTMLDivElement>(null)
    const [headerProgress, setHeaderProgress] = useState(0)
    const usesCustomFormCopy = landing.layout !== 'voice-escandallo'

    const title = t(`${prefix}.meta.title`)
    const description = t(`${prefix}.meta.description`)
    const canonicalUrl = absoluteLocalizedUrl(landing.path, locale)
    const pagePath = localizePath(landing.path, locale)

    const scrollToSection = (hash: string) => {
        const target = document.querySelector(hash)
        target?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        const onScroll = () => {
            const postHeroTop = postHeroRef.current?.getBoundingClientRect().top

            if (typeof postHeroTop !== 'number') return
            const fadeDistance = window.innerHeight * 0.2
            const nextProgress = Math.max(0, Math.min(1, (fadeDistance - postHeroTop) / fadeDistance))
            setHeaderProgress(nextProgress)
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const scrollToHashTarget = () => {
            if (window.location.hash === FORM_HASH) {
                scrollToSection(FORM_HASH)
            }
        }

        scrollToHashTarget()
        window.addEventListener('hashchange', scrollToHashTarget)
        return () => window.removeEventListener('hashchange', scrollToHashTarget)
    }, [])

    const goToForm = () => {
        window.history.replaceState(null, '', `${pagePath}${FORM_HASH}`)
        scrollToSection(FORM_HASH)
    }

    return (
        <div className="min-h-screen overflow-x-hidden bg-[#F5EEE4] font-['Archivo',system-ui,sans-serif] text-[#17130E] antialiased">
            <LocaleSeoLinks path={landing.path} locale={locale} />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <meta property='og:type' content='website' />
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:image' content={OG_IMAGE} />
                <meta name='twitter:card' content='summary_large_image' />
                <meta name='twitter:url' content={canonicalUrl} />
                <meta name='twitter:title' content={title} />
                <meta name='twitter:description' content={description} />
                <meta name='twitter:image' content={OG_IMAGE} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
                <script type='application/ld+json'>
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'WebPage',
                        name: title,
                        description,
                        url: canonicalUrl,
                        image: OG_IMAGE,
                        isPartOf: { '@id': TIPI_ORGANIZATION_ID },
                        about: {
                            '@type': 'SoftwareApplication',
                            name: 'Tipi',
                            applicationCategory: 'BusinessApplication',
                            operatingSystem: 'Web, iOS, Android',
                        },
                    })}
                </script>
            </PageHelmet>

            <FoodCostHeader
                progress={headerProgress}
                onCtaClick={goToForm}
                landingPage={landing.landingPage}
                ctaLabel={t(`${prefix}.hero.headerCta`)}
            />

            <main>
                <SeoKeywordHero landingId={landing.id} onCtaClick={goToForm} />
                <div ref={postHeroRef}>
                    <SeoKeywordLandingSections landing={landing} />
                    <FinalCtaSection
                        landingPage={landing.landingPage}
                        formTitle={usesCustomFormCopy ? t(`${prefix}.form.title`) : undefined}
                        formSubtitle={usesCustomFormCopy ? t(`${prefix}.form.subtitle`) : undefined}
                    />
                </div>
            </main>
            <Footer landingPage={landing.landingPage} className='mt-3 border-[#F5EEE4]/10 bg-[#17130E]' />
        </div>
    )
}
